import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { gql } from "@apollo/client";
import type { SyncablePriceListItemName } from "elephant-web/gql";
import { useMemo } from "react";
import type { RxCollection, RxDatabase, WithDeleted } from "rxdb";
import type { PriceListItemName as RxPriceListItemName } from "../collections/price-list-item-name";
import type { RxDBCollections } from "../provider";
import {
  SyncPriceListItemNamePullDocument,
  type WholeSyncablePriceListItemNameFragment,
} from "./__generated__/price-list-item-name.gql";
import {
  type ReplicationPullConfig,
  useGqlReplication,
} from "./use-gql-replication";

export const useReplicatePriceListItemName = ({
  db,
  collection,
}: {
  db?: RxDatabase<RxDBCollections>;
  collection?: RxCollection<RxPriceListItemName>;
}) => {
  const [enabled] = useFeatureFlag<boolean>(featureFlags.billing.priceListRxdb);

  const pull = useMemo<
    ReplicationPullConfig<WholeSyncablePriceListItemNameFragment> | undefined
  >(() => {
    if (!enabled) return undefined;
    return {
      query: SyncPriceListItemNamePullDocument,
      buildInputVariables: async (checkpoint, batchSize) => ({
        input: {
          limit: batchSize,
          checkpoint,
        },
      }),
      buildPullCheckpoint: async (doc) => ({
        id: doc.id,
        updatedAt: doc.meta.updatedAt,
      }),
      reSyncInterval: { minutes: 5 },
    };
  }, [enabled]);

  return useGqlReplication<
    RxPriceListItemName,
    SyncablePriceListItemName,
    null
  >({
    db,
    collection,
    pull,
    mapGqlToRx: mapGqlPriceListItemNameToRxPriceListItemName,
    batchSize: 100,
    metadataLocalStorageKeySuffix: "price-list-item-name",
  });
};

export const mapGqlPriceListItemNameToRxPriceListItemName = (
  itemName: WholeSyncablePriceListItemNameFragment,
): WithDeleted<RxPriceListItemName> => ({
  id: itemName.id,
  itemId: itemName.itemId,
  language: itemName.language,
  name: itemName.name,
  meta: {
    createdAt: itemName.meta.createdAt,
    createdByUserId: itemName.meta.createdByUserId,
    updatedAt: itemName.meta.updatedAt,
    updatedByUserId: itemName.meta.updatedByUserId,
  },
  _deleted: itemName.meta.deleted,
});

gql`
  fragment WholeSyncablePriceListItemName on SyncablePriceListItemName {
    id
    itemId
    language
    name
    meta {
      createdAt
      createdByUserId
      updatedAt
      updatedByUserId
      deleted
    }
  }
`;

gql`
  query SyncPriceListItemNamePull($input: PullPriceListItemNameInput!) {
    pullPriceListItemName(input: $input) {
      checkpoint {
        id
        updatedAt
      }
      documents {
        ...WholeSyncablePriceListItemName
      }
    }
  }
`;
